<template>
  <div :class="classes" @mouseenter="hovering = true" @mouseleave="hovering = false">
    <Transition name="v-slide-in-top">
      <label v-if="labelVisible" class="v-input__label" :for="id" :style="{ paddingLeft, paddingRight }">{{ label }}</label>
    </Transition>

    <div style="position: relative">
      <span v-if="$slots.prefix" ref="prefix" class="v-input__prefix">
        <slot name="prefix" />
      </span>

      <div class="v-input__inner" :style="{ paddingLeft, paddingRight: label ? '20px' : paddingRight }" @click.self="focus">
        <textarea
          ref="textarea"
          :disabled="isDisabled"
          :maxlength
          :minlength
          :placeholder="placeholder || label"
          :readonly
          :required="isRequired"
          :rows
          :style="{ resize }"
          :value="inputValue"
          @blur="onBlur"
          @focus="onFocus"
          @input="model = ($event.target as HTMLTextAreaElement).value"
          @keydown="onKeydown"
          @keyup="$emit('keyup', $event)"
          v-text="inputValue"
        />
      </div>

      <span class="v-textarea__suffix">
        <span v-if="hasSuffix" ref="suffix" class="v-input__suffix" :style="labelVisible ? `top: -${20}px; font-size: 12px` : undefined">
          <span v-if="isRequired" class="v-input__suffix-message">{{ i18n.required }}</span>
          <span v-if="optional" class="v-input__suffix-message">{{ i18n.optional }}</span>
          <span v-if="showCharacterLimit" class="v-input__length" v-text="characterLimitText" />

          <VIconHelp
            class="v-input__suffix-inner"
            :help
            icon-class="v-input__icon"
            :type="status === 'error' || errors.length > 0 ? 'red' : status === 'success' ? 'green' : status ? status : 'placeholder'"
          />

          <slot name="suffix" />
        </span>
      </span>
    </div>

    <VErrors :errors />

    <div v-if="suggestions.length > 0" class="v-input__suggestions">
      <VButton v-for="suggest in suggestions" :key="suggest" :label="suggest" size="small" type="secondary" @click="onClickSuggestion(suggest)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { get } from 'lodash-es'
import { computed, inject, ref, useId, useSlots, useTemplateRef, watch } from 'vue'

import { useClassName } from '../composables/useClassName.ts'
import { useErrors } from '../composables/useErrors.ts'
import { useResizeObserver } from '../composables/useResizeObserver.ts'
import type { VInputStatus, VTextareaResizeOption } from '../types.ts'
import { i18nInput as i18n } from '../utils/i18n.ts'
import { $formKey } from '../utils/injections.ts'
import VButton from './VButton.vue'
import VErrors from './VErrors.vue'
import VIconHelp from './VIconHelp.vue'

interface Props {
  disabled?: boolean
  error?: string | string[]
  help?: string | string[]
  label?: string
  maxlength?: number
  minlength?: number
  minimal?: boolean
  optional?: boolean
  placeholder?: string
  prop?: string
  readonly?: boolean
  required?: boolean
  resize?: VTextareaResizeOption
  rows?: number
  suggestions?: string[]
  showCharacterLimit?: boolean
  status?: VInputStatus
}

const props = withDefaults(defineProps<Props>(), {
  disabled: undefined,
  error: '',
  help: '',
  label: '',
  maxlength: undefined,
  minlength: undefined,
  minimal: undefined,
  optional: false,
  placeholder: '',
  prop: '',
  readonly: false,
  required: undefined,
  resize: 'vertical',
  rows: 2,
  showCharacterLimit: false,
  suggestions: () => [],
  status: '',
})

const model = defineModel<string | null>({ default: '' })

const emit = defineEmits<{
  keyup: [value: KeyboardEvent]
  keydown: [value: KeyboardEvent]
  blur: [value: FocusEvent]
  focus: [value: FocusEvent]
}>()

defineSlots<{
  prefix?(): any
  suffix?(): any
}>()

const slots = useSlots()
const form = inject($formKey, undefined)

const id = useId()
const hovering = ref(false)
const focused = ref(false)
const suffix = useTemplateRef('suffix')
const textarea = useTemplateRef('textarea')
const prefix = useTemplateRef('prefix')
const suffixWidth = ref(20)
const prefixWidth = ref(0)

const paddingLeft = computed(() => `${prefixWidth.value + (props.label ? 20 : 10)}px`)
const paddingRight = computed(() => `${suffixWidth.value}px`)
const labelVisible = computed(() => props.label && inputValue.value)
const inputValue = computed(() => (model.value ? String(model.value) : ''))
const hasSuffix = computed(() => isRequired.value || props.optional || slots.suffix || characterLimitText.value)
const isDisabled = computed(() => !!(props.disabled || (props.disabled === undefined && form?.props?.disabled)))
const isRequired = computed(() => {
  if (props.required) {
    return true
  }

  if (props.required === undefined && props.prop) {
    const rules = get(form?.props?.rules, props.prop, {})

    return 'required' in rules && rules.required
  }

  return false
})

const classes = useClassName(
  'v-textarea',
  computed(() => [props.label, props.placeholder]),
  computed(() => ({
    'v-input': true,
    'has-suffix': hasSuffix.value,
    'has-prefix': !!slots.prefix,
    'has-label': props.label,
    'has-input': inputValue.value,
    'is-disabled': isDisabled.value,
    'is-minimal': props.minimal,
    'is-required': isRequired.value,
    'is-optional': props.optional,
    focused: focused.value,
    hovering: hovering.value,
    [`v-input--${props.status}`]: props.status,
    [`v-input--${props.label ? 'large' : 'small'}`]: true,
    'v-input--error': props.status === 'error' || errors.value.length > 0,
  })),
)

const characterLimitText = computed(() => {
  if (!props.showCharacterLimit || props.maxlength === undefined) return ''

  const length = model.value ? String(model.value).length : 0

  return `${length} / ${props.maxlength}`
})

const errors = useErrors(props)

watch(
  () => props.prop,
  () => (props.prop ? form?.validateOneProp(props.prop) : undefined),
)

useResizeObserver(prefix, (_, target) => {
  prefixWidth.value = target.clientWidth
})

useResizeObserver(suffix, (_, target) => {
  suffixWidth.value = target.clientWidth + (labelVisible.value ? 10 : 5)
})

function onKeydown(event: KeyboardEvent) {
  emit('keydown', event)
  if (props.prop) {
    form?.validateOneProp(props.prop, 'keyup')
  }
}

function onFocus(event: FocusEvent) {
  focused.value = true
  emit('focus', event)
}

function onBlur(event: FocusEvent) {
  focused.value = false
  emit('blur', event)

  if (props.prop) {
    form?.validateOneProp(props.prop, 'blur')
  }
}

function focus() {
  textarea.value?.focus()
}

function onClickSuggestion(suggestion: string) {
  model.value = suggestion
  focus()
}

defineExpose({
  textarea,
  characterLimitText,
  errors,
  focus,
  paddingLeft,
  paddingRight,
})
</script>

<style>
.v-textarea .v-input__inner {
  cursor: text;

  > textarea {
    border: none;
    outline: none;
    width: 100%;
    padding: 0;
    min-height: 30px;
    line-height: 1.3;

    &::placeholder {
      color: var(--color-text-placeholder);
    }
  }
}

.v-textarea__suffix {
  position: absolute;
  right: 0;
  top: 16px;
}

.v-textarea.has-label .v-textarea__suffix {
  top: 26px;
}
</style>
